import { RT_FRAUD_SOURCE_IDS } from '../../constants/cmConstants';
import { formatScore as formatChipScore } from '../framl/components/AlertSelector';

// FIN-11361: the effective customer id for a case-summary card — the explicitly
// selected customer, else the case's first customer. (Was copy-pasted in every
// card container.)
// FIN-13215 — Case Fraud/AML card copy when no customer is selected and the case has none:
// say so instead of a bare "(0)".
export const NO_CUSTOMER_TEXT = 'No customer is linked to this case.';

export const resolveCardCustomerId = (selectedCustomerId, caseDetails) =>
  selectedCustomerId || caseDetails?.acase?.customers?.[0]?.id;

// FIN-11361: score formatter shared by the alert cards — em-dash for a missing score.
// FIN-13891: delegate to the FIN-13184 formatter (2 decimals, same as the TM alert page)
// instead of flooring, which read 99.9997 as 99 next to a 100.00 badge on the same page.
export const formatScore = (score) => formatChipScore(score) ?? '—';

// Wave-1 gate seam for the CM FRAML Summary tab (FIN-11361). The case-detail alert
// carries `sourceId` as a string code (cmConstants ALERT_SOURCE_IDS); RT_FRAUD_SOURCE_IDS
// are the realtime-fraud (FRAML-P) codes. Wave 2 swaps this predicate to snapshot-row
// existence (design v3 AD-6) without changing callers.
export const isFramlCase = (caseDetails) =>
  Array.isArray(caseDetails?.alerts) &&
  caseDetails.alerts.some((a) =>
    RT_FRAUD_SOURCE_IDS.includes(a?.alert?.sourceId)
  );

// Apply the backend-ordered score list (score DESC, from the caseAlertScores
// endpoint) to a card's alert rows. The DB decides the order; the FE only follows
// it — walk the ordered list and emit each matching row with its score merged in.
// No client sort.
//
// FIN-13215 — the score list is now the MEMBERSHIP list too, so rows it does not
// name are dropped rather than appended.
//
// It used to append every unmatched row at the end. That is what made the Case
// Fraud/AML cards show the same alert under every customer in the drop-down: the
// list was case-wide, and the tail put back anything the score list left out.
// Now that caseAlertScores is scoped to the alerts the selected customer is a
// party to, keeping the tail would put the leak straight back.
//
// Safe to drop: `caseAlertScores` skips NULL scores, and no cased alert has one —
// 0 of 2,234 cased fraud alerts and 0 of 7,641 cased AML alerts on keycloak-qa.
// The tail was therefore already unreachable for real data. If a NULL-score alert
// ever appears it will be absent from the card, which is the correct reading of
// "this customer's alerts" and is visible rather than silently misattributed.
export const mergeScoresInOrder = (rows, scoreList) => {
  const list = Array.isArray(scoreList) ? scoreList : [];
  const byId = new Map();
  (rows || []).forEach((row) => {
    if (row && row.alert) byId.set(row.alert.id, row);
  });
  const ordered = [];
  list.forEach(({ alertId, score }) => {
    const row = byId.get(alertId);
    if (row) ordered.push({ ...row, alert: { ...row.alert, score } });
  });
  return ordered;
};

// FIN-11361 — per-alert action gating that MATCHES the BE.
// cm-be enforces alert actions by the alert's OWNERSHIP relative to the user
// (own / others / unassigned → distinct capabilities). The FE previously gated
// the Resolve/Add buttons with the OR of all three (getAccessToConvertOrMergeAlerts),
// so a user holding only one capability saw the button enabled on every row and
// only got rejected at the API. These helpers tighten the FE to the per-row rule,
// mirroring the canonical logic in alertList/AlertList.js (assignedUser.username
// for own/others, metadata.status === 'UNASSIGNED' for unassigned).

// Classify an alert row's ownership relative to the current user.
// Accepts either an assignee username (case-alert rows = AlertFullWithCase) or an
// assignee id (new/uncased rows that only carry assignedUsernameId). Returns one
// of 'own' | 'others' | 'unassigned'.
export const alertOwnership = (
  { status, assigneeUsername, assigneeId } = {},
  currentUser
) => {
  if (status === 'UNASSIGNED') return 'unassigned';
  if (assigneeUsername != null && assigneeUsername !== '') {
    return assigneeUsername === currentUser?.username ? 'own' : 'others';
  }
  if (assigneeId != null) {
    return assigneeId === currentUser?.id ? 'own' : 'others';
  }
  return 'unassigned';
};

// Given an ownership class and the three per-ownership capabilities, can the user
// act? Defaults to false when a capability is missing (deny, matching the BE).
export const canActByOwnership = (
  ownership,
  { hasOwn, hasOthers, hasUnassigned } = {}
) => {
  if (ownership === 'own') return !!hasOwn;
  if (ownership === 'others') return !!hasOthers;
  return !!hasUnassigned;
};

// FIN-11361: resolve the CONFIGURED display name for a status code from the CM
// Status Name Settings (cmWorkFlowStatus.workFlowStatusList), matching the rest of
// CM (AlertDetails.getConfirmPopUpStatusLabel). Falls back to the default label.
export const resolveStatusLabel = (workFlowStatusList, code = 'NO_ISSUE') => {
  const s = (workFlowStatusList || []).find((x) => x.code === code);
  return s?.currentValue || s?.defaultValue || 'No Issue';
};

// FIN-12897: map the raw status code (e.g. NO_ISSUE) to the admin-configured display
// name via the SHARED CM resolver (resolveStatusLabel above — same source as
// the FRAML Summary tab), so the expand can never drift from the rest of CM. Codes
// absent from the config list fall back to a title-cased prettified code so an
// unknown status never renders as a shouty ENUM (or as resolveStatusLabel's
// 'No Issue' default, which would be wrong for e.g. WORK_IN_PROGRESS).
export const statusLabel = (code, list) => {
  const c = (code || '').toString();
  const found = (list || []).find(
    (s) => (s.code || '').toUpperCase() === c.toUpperCase()
  );
  // FIN-12916 — the `found &&` guard alone was not enough. resolveStatusLabel returns the
  // literal 'No Issue' whenever the matched entry carries neither currentValue nor
  // defaultValue, so a configured-but-unnamed status (e.g. ON_HOLD with no display name)
  // rendered as "No Issue" — a DIFFERENT, terminal status. Only delegate when the entry
  // actually has a name; otherwise fall through to the prettified code, which is what the
  // comment above always intended.
  if (found && (found.currentValue || found.defaultValue)) {
    return resolveStatusLabel(list, found.code);
  }
  return c
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase());
};

// Converts a hex color (#rrggbb or #rgb) to rgba(r,g,b,alpha).
// Falls back to the supplied fallback string when hex is absent/invalid.
// FIN-13941 — shared so every surface that tints a configured level colour (the case Live Risk
// band banner, the FRAML View Alert Level chip) derives its background/border the same way.
export const hexToRgba = (hex, alpha, fallback) => {
  if (!hex || typeof hex !== 'string') return fallback;
  const h = hex.replace('#', '');
  const full =
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h;
  if (full.length !== 6) return fallback;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

// FIN-11361 (D13): resolve the CONFIGURED colour for an alert/FRAML level from the CM
// Level Name Settings (cmWorkFlowStatus.alertLevels). Matches by the same keys as
// resolveLevelLabel (code / defaultValue / defaultName, case-insensitive).
// ponytail: neutral #9ca3af on miss — never falls back to score thresholds.
// `fallback` overrides the miss colour for callers with their own palette (FIN-13973).
export const resolveLevelColor = (alertLevels, code, fallback = '#9ca3af') => {
  const NEUTRAL = fallback;
  if (code == null || code === '') return NEUTRAL;
  const v = String(code).toUpperCase();
  const lvl = (alertLevels || []).find((l) =>
    [l && l.code, l && l.defaultValue, l && l.defaultName].some(
      (x) => x != null && String(x).toUpperCase() === v
    )
  );
  return (lvl && lvl.color) || NEUTRAL;
};

// FIN-11361 (D13): resolve the CONFIGURED display name for an alert/FRAML level from
// the CM Level Name Settings (cmWorkFlowStatus.alertLevels). `value` may be a level
// code or a band label (High/Good/Low). Matches case-insensitively against the
// level's code / default name; returns the configured displayName, else the default,
// else the raw value. Auto-reflects admin relabels (e.g. "Good" → "GOODDUDE").
//
// FIN-13794 (adversarial review P2-R6) — under Q16 revised the pipeline no longer stamps a
// band, and the derivation may return None for a sub-lowMin customer. Render "Below
// threshold" instead of a blank chip so the LIVE and CASE-SNAPSHOT sides look consistent
// with the FRAML customer-detail header (which already renders the same label for None).
export const resolveLevelLabel = (alertLevels, value) => {
  if (value == null || value === '') return 'Below threshold';
  const v = String(value).toUpperCase();
  const lvl = (alertLevels || []).find((l) =>
    [l && l.code, l && l.defaultValue, l && l.defaultName].some(
      (x) => x != null && String(x).toUpperCase() === v
    )
  );
  if (!lvl) return value;
  return lvl.displayName || lvl.defaultValue || lvl.defaultName || value;
};
